// Shared date-format catalog for the import wizard + runner. The `key` values
// MUST stay in sync with internal/importer/dates.go (dateLayout). "" = leave the
// value as-is (no conversion).

export const DATE_FORMATS = [
  { key: '',            label: 'As-is (text)' },
  { key: 'iso',         label: 'YYYY-MM-DD' },
  { key: 'iso_dt',      label: 'YYYY-MM-DD HH:mm:ss' },
  { key: 'dmy',         label: 'DD/MM/YYYY' },
  { key: 'dmy_dash',    label: 'DD-MM-YYYY' },
  { key: 'dmy_s',       label: 'D/M/YYYY' },
  { key: 'mdy',         label: 'MM/DD/YYYY' },
  { key: 'mdy_s',       label: 'M/D/YYYY' },
  { key: 'mdy_dash_yy', label: 'MM-DD-YY' },
  { key: 'dmy_dt',      label: 'DD/MM/YYYY HH:mm:ss' },
  { key: 'mdy_dt',      label: 'MM/DD/YYYY HH:mm:ss' },
  { key: 'dmon_y',      label: 'DD MMM YYYY' },
];

// Parse specs for the live preview only (the real conversion happens server-side
// in importer.NormalizeDates). Each maps capture groups to date fields.
const SPECS = {
  iso:         { re: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, f: ['y', 'm', 'd'] },
  iso_dt:      { re: /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/, f: ['y', 'm', 'd', 'H', 'M', 'S'] },
  dmy:         { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, f: ['d', 'm', 'y'] },
  dmy_dash:    { re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, f: ['d', 'm', 'y'] },
  dmy_s:       { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, f: ['d', 'm', 'y'] },
  mdy:         { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, f: ['m', 'd', 'y'] },
  mdy_s:       { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, f: ['m', 'd', 'y'] },
  mdy_dash_yy: { re: /^(\d{1,2})-(\d{1,2})-(\d{2})$/, f: ['m', 'd', 'yy'] },
  dmy_dt:      { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/, f: ['d', 'm', 'y', 'H', 'M', 'S'] },
  mdy_dt:      { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/, f: ['m', 'd', 'y', 'H', 'M', 'S'] },
  dmon_y:      { re: /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/, f: ['d', 'mon', 'y'] },
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const MON3 = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// previewDate parses `value` with the given format key and returns a human
// "DD MMMM YYYY" string (e.g. "23 May 2025"), or null if it doesn't parse.
export function previewDate(value, key) {
  if (!key) return null;
  const v = value == null ? '' : String(value).trim();
  if (!v) return null;
  const spec = SPECS[key];
  if (!spec) return null;
  const m = v.match(spec.re);
  if (!m) return null;
  const get = (name) => { const i = spec.f.indexOf(name); return i >= 0 ? m[i + 1] : undefined; };
  let year = get('y');
  const yy = get('yy');
  if (yy != null) { const n = parseInt(yy, 10); year = String(n < 69 ? 2000 + n : 1900 + n); } // pivot matches Go
  let month;
  const mon = get('mon');
  if (mon != null) { month = MON3.indexOf(mon.slice(0, 3).toLowerCase()) + 1; if (month === 0) return null; }
  else month = parseInt(get('m'), 10);
  const day = parseInt(get('d'), 10);
  if (!year || !month || !day || month > 12 || day > 31) return null;
  return `${String(day).padStart(2, '0')} ${MONTHS[month - 1]} ${year}`;
}

// isDateType reports whether a MySQL column type is date-ish.
export function isDateType(type) { return /^(date|datetime|timestamp)/i.test(String(type || '')); }

// looksLikeDate is a cheap heuristic for sniffing a sample cell value.
export function looksLikeDate(value) { return /\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}/.test(String(value || '')); }
